import { Upload, AlertTriangle, Wifi, MapPin, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AdminTabShell } from './AdminTabShell';
import {
  useAdminOnboardingDiagnostics,
  type ImportJob,
  type FailedImportQueueItem,
  type ProviderSessionHealth,
  type ProvisionalProperty,
  type OnboardingIncompleteProperty,
} from '@/hooks/admin/useAdminOnboardingDiagnostics';

function SimpleTable<T extends Record<string, unknown>>({ rows, columns, emptyText }: {
  rows: T[];
  columns: { key: string; label: string; render?: (row: T) => React.ReactNode }[];
  emptyText?: string;
}) {
  if (rows.length === 0) return <p className="text-xs text-wt-text-muted py-2">{emptyText ?? 'None'}</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead><tr className="text-wt-text-muted border-b border-wt-border">
          {columns.map(c => (
            <th key={c.key} className="text-left py-1.5 pr-3 font-medium">{c.label}</th>
          ))}
        </tr></thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={(row.id as string) ?? i} className="border-b border-wt-border/50">
              {columns.map(c => (
                <td key={c.key} className="py-1.5 pr-3">
                  {c.render ? c.render(row) : String(row[c.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString();
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

function statusVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (status === 'complete' || status === 'completed') return 'default';
  if (status === 'failed') return 'destructive';
  if (status === 'running' || status === 'processing') return 'outline';
  return 'secondary';
}

export function OnboardingDiagnosticsTab() {
  const { data: d, isLoading, error } = useAdminOnboardingDiagnostics();

  return (
    <AdminTabShell isLoading={isLoading} error={error}>
      {d && (
        <div className="space-y-4">
          {/* Health summary */}
          {d.failed_import_queue.length === 0 &&
           d.provider_session_health.every(ps => ps.expired_sessions === 0) &&
           d.provisional_properties.length === 0 &&
           d.onboarding_incomplete.length === 0 && (
            <div className="flex items-center gap-2 text-xs text-green-500 py-1">
              <CheckCircle2 className="h-4 w-4" />
              All onboarding systems healthy — no issues detected.
            </div>
          )}

          {/* Provider Session Health */}
          <Card className="border-wt-border bg-wt-bg-secondary">
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-xs text-wt-text-muted font-normal">Provider Session Health</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-3">
              {d.provider_session_health.length === 0 ? (
                <p className="text-xs text-wt-text-muted">No sessions</p>
              ) : (
                <div className="flex flex-wrap gap-3">
                  {d.provider_session_health.map(ps => (
                    <div key={ps.provider_type} className="flex items-center gap-2 text-xs">
                      <Wifi className={`h-3 w-3 ${ps.expired_sessions > 0 ? 'text-wt-amber-primary' : 'text-wt-text-muted'}`} />
                      <span className="font-mono">{ps.provider_type}</span>
                      <Badge variant="default" className="text-[10px]">{ps.active_sessions} active</Badge>
                      {ps.expired_sessions > 0 && (
                        <Badge variant="destructive" className="text-[10px]">{ps.expired_sessions} expired</Badge>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Recent Import Jobs */}
            <Card className="border-wt-border bg-wt-bg-secondary">
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Upload className="h-4 w-4 text-wt-text-muted" />
                  Import Jobs (30d) ({d.recent_import_jobs.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-3">
                <SimpleTable<ImportJob>
                  rows={d.recent_import_jobs}
                  emptyText="No import jobs in last 30 days"
                  columns={[
                    { key: 'org_name', label: 'Org' },
                    { key: 'provider_type', label: 'Provider', render: r => <span className="font-mono">{r.provider_type}</span> },
                    { key: 'status', label: 'Status', render: r => (
                      <Badge variant={statusVariant(r.status)} className="text-[10px]">{r.status}</Badge>
                    )},
                    { key: 'processed_plants', label: 'Plants', render: r => <>{r.processed_plants}/{r.total_plants}</> },
                    { key: 'created_at', label: 'Created', render: r => formatDate(r.created_at) },
                  ]}
                />
              </CardContent>
            </Card>

            {/* Failed Import Queue Items */}
            <Card className="border-wt-border bg-wt-bg-secondary">
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-wt-red-primary" />
                  Failed Queue Items (30d) ({d.failed_import_queue.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-3">
                <SimpleTable<FailedImportQueueItem>
                  rows={d.failed_import_queue}
                  emptyText="No failed queue items in last 30 days"
                  columns={[
                    { key: 'provider_plant_id', label: 'Plant ID', render: r => <span className="font-mono">{r.provider_plant_id ?? '—'}</span> },
                    { key: 'error', label: 'Error', render: r => (
                      <span className="text-wt-red-primary max-w-[200px] truncate block" title={r.error ?? undefined}>{r.error ?? '—'}</span>
                    )},
                    { key: 'updated_at', label: 'Failed', render: r => formatDateTime(r.updated_at) },
                  ]}
                />
                {d.failed_import_queue.length > 0 && (
                  <p className="text-[10px] text-wt-text-muted mt-1.5">Check credentials and provider connectivity. Re-import if transient.</p>
                )}
              </CardContent>
            </Card>

            {/* Provisional Properties */}
            <Card className="border-wt-border bg-wt-bg-secondary">
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-wt-amber-primary" />
                  Provisional Properties ({d.provisional_properties.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-3">
                <SimpleTable<ProvisionalProperty>
                  rows={d.provisional_properties}
                  emptyText="No provisional properties"
                  columns={[
                    { key: 'name', label: 'Name' },
                    { key: 'address', label: 'Address' },
                    { key: 'org_name', label: 'Org' },
                    { key: 'created_at', label: 'Created', render: r => formatDate(r.created_at) },
                  ]}
                />
                {d.provisional_properties.length > 0 && (
                  <p className="text-[10px] text-wt-text-muted mt-1.5">Awaiting plant claim or import. Use Property Lookup to inspect.</p>
                )}
              </CardContent>
            </Card>

            {/* Onboarding Incomplete */}
            <Card className="border-wt-border bg-wt-bg-secondary">
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-wt-amber-primary" />
                  Onboarding Incomplete ({d.onboarding_incomplete.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-3">
                <SimpleTable<OnboardingIncompleteProperty>
                  rows={d.onboarding_incomplete}
                  emptyText="All non-provisional properties fully onboarded"
                  columns={[
                    { key: 'name', label: 'Name' },
                    { key: 'org_name', label: 'Org' },
                    { key: 'readiness_state', label: 'Readiness' },
                    { key: 'data_source_mode', label: 'Data Source' },
                    { key: 'created_at', label: 'Created', render: r => formatDate(r.created_at) },
                  ]}
                />
                {d.onboarding_incomplete.length > 0 && (
                  <p className="text-[10px] text-wt-text-muted mt-1.5">Review missing setup steps. Use Property Lookup for details.</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </AdminTabShell>
  );
}
